import {assert} from 'Asserts';
import {getTagNameFromSortKey, isTagSortKey} from 'schemas/tags/ExpandedTableTagUtil';
import {MultiSortKey, Sort, SortKey} from 'search/SearchConstants';
import {Sortable} from 'search/SortForm';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';

export function isRelevanceSort(sorts: Array<Sort>): boolean {
  if (sorts.length && sorts[0].key === SortKey.SORT_RELEVANCE_KEY) {
    assert(sorts.length === 1, 'Sorting by relevance is not allowed with multiple sort keys');
    return true;
  }
  return false;
}

export function isChemicalSimilaritySort(sorts: Array<Sort>): boolean {
  return sorts.find((sort) => sort.key === SortKey.SORT_CHEMICAL_SIMILARITY) != null;
}

export function isBLASTSort(sorts: Array<Sort>): boolean {
  return Boolean(sorts.find((sort) => sort.key === SortKey.SORT_BLAST_E_VALUE));
}

export function pickSortableColumns(columns: Array<ScrollableTableColumn>): Array<Sortable> {
  return columns
    .filter((column) => column.isSortable)
    .map((column): Sortable => {
      assert(Boolean(column.sortKey), `Sortable column ${column.header} is missing sortKey`);
      return {
        ...column,
        sortKey: column.sortKey!,
        sortDirection: column.sortDirection!,
      };
    });
}

export function getColumnHeaderFromSortKey(sortKey: string): string {
  if (isTagSortKey(sortKey)) {
    return getTagNameFromSortKey(sortKey);
  }
  return SORT_KEY_TO_COLUMN_HEADER[sortKey as SortKey];
}

export function getHeaderFromMultiSortKey(multiSortKey: string): string {
  assert(
    Object.keys(MULTI_SORT_KEY_TO_HEADER).includes(multiSortKey),
    `Multi-sort key ${multiSortKey} is not supported`
  );
  return MULTI_SORT_KEY_TO_HEADER[multiSortKey as MultiSortKey];
}

export function pruneInvisibleSorts(sorts: Array<Sort>): Array<Sort> {
  return sorts.filter((sort) => sort.key !== SortKey.SORT_STARRED);
}

const SORT_KEY_TO_COLUMN_HEADER: {[key in SortKey]: string} = {
  [SortKey.SORT_ARCHIVED_PURPOSE]: 'Archive purpose',
  [SortKey.SORT_BARCODE_KEY]: 'Barcode',
  [SortKey.SORT_BLAST_E_VALUE]: 'E value',
  [SortKey.SORT_REGISTRY_IDENTIFIER_KEY]: 'ID',
  [SortKey.SORT_CREATED_AT_KEY]: 'Created',
  [SortKey.SORT_NAME_KEY]: 'Name',
  [SortKey.SORT_IDENTIFIER_KEY]: 'ID',
  [SortKey.SORT_MODIFIED_AT_KEY]: 'Modified',
  [SortKey.SORT_WORKLIST_POSITION_KEY]: 'Position',
  [SortKey.SORT_RELEVANCE_KEY]: 'Relevance',
  [SortKey.SORT_SCHEDULED_ON_KEY]: 'Scheduled on',
  [SortKey.SORT_SCHEMA_NAME]: 'Schema',
  [SortKey.SORT_CREATOR_NAME]: 'Creator',
  [SortKey.SORT_EXECUTION_TYPE]: 'Execution type',
  [SortKey.SORT_EXPIRATION_DATE]: 'Expiration date',
  [SortKey.SORT_FREEZE_THAW_COUNT]: 'Freeze/thaw count',
  [SortKey.SORT_RECIPE_TEMPLATE_NAME]: 'Recipe',
  [SortKey.SORT_ASSAY_TEMPLATE_NAME]: 'Assay',
  [SortKey.SORT_PROCESS_ID]: 'Task group ID',
  [SortKey.SORT_ASSIGNEE_NAME]: 'Assignee',
  [SortKey.SORT_STATUS]: 'Status',
  [SortKey.SORT_PROCESS_TASK_ID]: 'Task ID',
  [SortKey.SORT_PROCESS_TASK_TYPE]: 'Task type',
  [SortKey.SORT_CHEMICAL_SIMILARITY]: 'Chemical similarity',
  [SortKey.SORT_SAMPLE_RESTRICTION_STATUS]: 'Restriction status',
  [SortKey.SORT_STARRED]: 'Starred',
  [SortKey.SORT_CONDITION_NUMBERS]: 'Condition order',
  [SortKey.SORT_REPLICATE_NUMBERS]: 'Replicate order',
  [SortKey.SORT_PROCEDURE_METHOD_FLOWCHART_ORDER]: 'Flowchart order',
  [SortKey.SORT_RECIPE_METHOD_RECIPE_ORDER]: 'Recipe order',
  [SortKey.SORT_RECIPE_STEP_RECIPE_ORDER]: 'Recipe order',
  [SortKey.SORT_ASSAY_METHOD_ASSAY_ORDER]: 'Assay order',
  [SortKey.SORT_ASSAY_STEP_ASSAY_ORDER]: 'Assay order',
  [SortKey.SORT_REQUEST_V2_SUBMISSION_STATUS]: 'Request status',
  [SortKey.SORT_REQUEST_V2_DEFINITION_NAME]: 'Request type',
  [SortKey.SORT_PRIMARY_PRODUCT_YIELD]: 'Yield',
};

const MULTI_SORT_KEY_TO_HEADER: {[key in MultiSortKey]: string} = {
  [MultiSortKey.SORT_RECIPE_ORDER]: 'Recipe order',
};
